'use client';

import clsx from 'clsx';
import { MenuItemType } from '@akinon/next/types';
import { Icon, Link } from '@theme/components';
import { Image } from '@akinon/next/components/image';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import * as Tabs from '@radix-ui/react-tabs';
import { MenuList } from './menu-list';

interface NavbarProps {
  menu: MenuItemType[];
}

export default function Navbar(props: NavbarProps) {
  const { menu } = props;

  return (
    <>
      <NavigationMenu.Root className="absolute z-[2] flex left-0 right-0 h-25">
        <NavigationMenu.List className="flex list-none space-x-8 items-center h-full px-8 text-sm font-medium *:h-full">
          {menu.map(
            (item, index) =>
              item.label != null && (
                <NavigationMenu.Item key={index}>
                  {item.children.length > 0 ? (
                    <>
                      <NavigationMenu.Trigger className="group flex items-center text-primary-black h-full">
                        {item.label}
                        <Icon
                          name="chevron-down"
                          size={24}
                          className="relative top-px text-violet10 transition-transform duration-[250] ease-in group-data-[state=open]:-rotate-180"
                          aria-hidden
                        />
                      </NavigationMenu.Trigger>
                      <NavigationMenu.Content className="absolute left-0 top-0 w-full h-[32rem] 2xl:h-[39rem] flex justify-between items-stretch z-[2]">
                        <Tabs.Root
                          orientation="vertical"
                          defaultValue={item.children[0]?.pk}
                          className="flex items-stretch pl-8 pt-8 pb-12 pr-8 3xl:pl-15 3xl:pt-15 3xl:pb-20 3xl:pr-12 h-full flex-grow"
                        >
                          <Tabs.List
                            aria-label="tabs menu"
                            className="h-full flex flex-col text-left items-start space-y-2 border-r border-[#CDCFCE] py-5"
                          >
                            {item.children.map((child, index) => (
                              <Tabs.Trigger key={index} value={child.pk}>
                                <div className="flex items-center flex-1 min-w-[15.625rem]">
                                  {child?.extra_context?.attributes
                                    ?.main_image && (
                                    <Image
                                      src={
                                        child?.extra_context?.attributes
                                          ?.main_image?.kwargs?.url
                                      }
                                      alt={child.label}
                                      width={48}
                                      height={48}
                                      className="flex-none max-w-12"
                                      priority
                                    />
                                  )}
                                  <span className="pl-6 flex-grow text-left text-lg">
                                    {child.label}
                                  </span>
                                </div>
                              </Tabs.Trigger>
                            ))}
                          </Tabs.List>
                          {item.children.map((child, index) => (
                            <Tabs.Content
                              key={index}
                              value={child.pk}
                              className="h-full flex-1 pl-8 pr-4 2xl:pl-20"
                            >
                              <MenuList
                                item={child}
                                LinkComponent={NavigationMenu.Link}
                                className={clsx([
                                  'flex flex-wrap text-sm font-normal',
                                  child.extra_context?.attributes
                                    ?.children_direction?.value !== 'row' &&
                                    'flex-col',
                                  child.extra_context?.attributes
                                    ?.children_direction?.value === 'row' &&
                                    'flex-row'
                                ])}
                              />
                            </Tabs.Content>
                          ))}
                        </Tabs.Root>
                        <NavigationMenu.Link href="/" className="h-full hidden">
                          <Image
                            src="/menu-banner.jpg"
                            width={480}
                            height={620}
                            alt="Menu Image"
                            className="h-full w-auto"
                            imageClassName="h-full w-full object-cover"
                          />
                        </NavigationMenu.Link>
                      </NavigationMenu.Content>
                    </>
                  ) : (
                    <NavigationMenu.Link
                      href={item.url}
                      className="text-primary-black h-full flex items-center"
                    >
                      {item.label}
                    </NavigationMenu.Link>
                  )}
                </NavigationMenu.Item>
              )
          )}
        </NavigationMenu.List>

        <div className="perspective-[2000px] absolute left-0 top-full flex w-full justify-center">
          <NavigationMenu.Viewport className="relative h-[var(--radix-navigation-menu-viewport-height)] origin-[top] overflow-hidden bg-[#FAF4ED] transition-[width,_height] duration-300 data-[state=closed]:animate-scaleOut data-[state=open]:animate-scaleIn w-dvw" />
        </div>
      </NavigationMenu.Root>
    </>
  );
}
